import { useState } from 'react';
import { ProgressBar } from 'primereact/progressbar';
import MlxButton from '../../../../../../components/common/MlxButton/MlxButton';
import MlxDialog from '../../../../../../components/common/MlxDialog/MlxDialog';
import { JTranslation } from '../../../../../../helpers/jTranslationHelper';
import { useTranslation } from '../../../../../../hooks/useTranslation';
import { _MenuImportBatchSummary, _MenuImportStatus } from '../../../../../../helpers/menuImportHelper';
import {
    MENU_IMPORT_CANCEL_CONFIRM_MSG,
    MENU_IMPORT_CANCEL_CONFIRM_TITLE,
    MENU_IMPORT_CANCEL_IMPORT,
    MENU_IMPORT_EXTRACTING,
    MENU_IMPORT_EXTRACTING_DESC,
    MENU_IMPORT_FILES_COUNT,
    MENU_IMPORT_KEEP_IMPORTING,
    MENU_IMPORT_MENU_LABEL,
    MENU_IMPORT_PAGES_FOUND,
    MENU_IMPORT_STEP_ITEMS,
    MENU_IMPORT_STEP_PREPARING,
    MENU_IMPORT_STEP_READING,
    MENU_IMPORT_STEP_SECTIONS,
    MENU_IMPORT_UPLOADING,
    MENU_IMPORT_UPLOADING_DESC,
} from '../../../../../../constants/strings';

const languageTexts = { PROGRESS_LABEL: MENU_IMPORT_EXTRACTING };

const CHECKLIST = [MENU_IMPORT_STEP_READING, MENU_IMPORT_STEP_SECTIONS, MENU_IMPORT_STEP_ITEMS, MENU_IMPORT_STEP_PREPARING];

// How many checklist rows are ticked for a status; a batch that is not created yet ticks none.
const DONE_COUNT: Partial<Record<_MenuImportStatus, number>> = {
    SPLITTING: 1,
    EXTRACTING: 2,
    CONSOLIDATING: 3,
};

type _ExtractionStepProps = {
    batch?: _MenuImportBatchSummary;
    uploading?: boolean;
    fileCount?: number;
    fileNames?: string[];
    /** The name typed for the new menu, shown in the footer bar when known. */
    menuName?: string;
    onCancel: () => void;
};

const ExtractionStep = ({ batch, uploading = false, fileCount, fileNames, menuName, onCancel }: Readonly<_ExtractionStepProps>) => {
    const [confirmVisible, setConfirmVisible] = useState(false);
    const translatedText = useTranslation(languageTexts, 'none');

    const progress = batch?.progress;
    const totalChunks = progress?.totalChunks ?? 0;
    const percent = totalChunks > 0 ? Math.round(((progress?.processedChunks ?? 0) / totalChunks) * 100) : 0;
    const doneCount = uploading || !batch ? 0 : (DONE_COUNT[batch.status] ?? 0);
    const files = fileCount ?? batch?.fileCount;

    return (
        <div className="bulk-upload-card bulk-upload-step-card">
            <div className="bulk-upload-state">
                <div className="bulk-upload-state-heading">
                    <span className="bulk-upload-state-title">
                        <JTranslation text={uploading ? MENU_IMPORT_UPLOADING : MENU_IMPORT_EXTRACTING} />
                    </span>
                    <i className="ri-loader-4-line bulk-upload-spinner" aria-hidden="true" />
                </div>
                <span className="bulk-upload-muted">
                    <JTranslation text={uploading ? MENU_IMPORT_UPLOADING_DESC : MENU_IMPORT_EXTRACTING_DESC} />
                </span>

                <ProgressBar
                    className="bulk-upload-progress"
                    mode={totalChunks > 0 && !uploading ? 'determinate' : 'indeterminate'}
                    value={percent}
                    showValue={false}
                    aria-label={translatedText.PROGRESS_LABEL ?? MENU_IMPORT_EXTRACTING}
                />

                {uploading && !!fileNames?.length && (
                    <span className="bulk-upload-muted bulk-upload-meta-line" data-testid="uploading-files">
                        {fileNames.join(' · ')}
                    </span>
                )}

                {!uploading && (
                    <>
                        {(!!progress?.totalPages || !!files) && (
                            <span className="bulk-upload-muted bulk-upload-meta-line">
                                {!!progress?.totalPages && (
                                    <span data-testid="extraction-pages">
                                        <JTranslation text={MENU_IMPORT_PAGES_FOUND} /> {progress.totalPages}
                                    </span>
                                )}
                                {!!progress?.totalPages && !!files && <span aria-hidden="true">·</span>}
                                {!!files && (
                                    <span data-testid="extraction-files">
                                        <JTranslation text={MENU_IMPORT_FILES_COUNT} /> {files}
                                    </span>
                                )}
                            </span>
                        )}
                        <ul className="bulk-upload-checklist">
                            {CHECKLIST.map((label, index) => {
                                const done = index < doneCount;
                                const current = index === doneCount;
                                let icon = 'ri-checkbox-blank-circle-line';
                                if (done) icon = 'ri-checkbox-circle-fill';
                                else if (current) icon = 'ri-loader-4-line bulk-upload-spinner';
                                let rowClass = '';
                                if (done) rowClass = 'done';
                                else if (current) rowClass = 'current';
                                return (
                                    <li key={label} className={rowClass} data-testid={`checklist-${index}`}>
                                        <i className={icon} />
                                        <JTranslation text={label} />
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
            </div>

            {!uploading && (
                <div className="bulk-upload-footer-bar" data-testid="extraction-footer">
                    <span className="bulk-upload-muted" data-testid="extraction-menu-name">
                        {!!menuName && (
                            <>
                                <JTranslation text={MENU_IMPORT_MENU_LABEL} /> <strong>{menuName}</strong>
                            </>
                        )}
                    </span>
                    <MlxButton outlined onClick={() => setConfirmVisible(true)}>
                        <JTranslation text={MENU_IMPORT_CANCEL_IMPORT} />
                    </MlxButton>
                </div>
            )}

            <MlxDialog
                visible={confirmVisible}
                onHide={() => setConfirmVisible(false)}
                header={<JTranslation text={MENU_IMPORT_CANCEL_CONFIRM_TITLE} />}
                footer={
                    <div className="d-flex justify-content-end gap-2">
                        <MlxButton outlined onClick={() => setConfirmVisible(false)}>
                            <JTranslation text={MENU_IMPORT_KEEP_IMPORTING} />
                        </MlxButton>
                        <MlxButton
                            onClick={() => {
                                setConfirmVisible(false);
                                onCancel();
                            }}
                        >
                            <JTranslation text={MENU_IMPORT_CANCEL_IMPORT} />
                        </MlxButton>
                    </div>
                }
            >
                <p className="m-0">
                    <JTranslation text={MENU_IMPORT_CANCEL_CONFIRM_MSG} />
                </p>
            </MlxDialog>
        </div>
    );
};

export default ExtractionStep;
